/**
 * Signal durability types for the frontend.
 *
 * Mirrors the public enums in
 * supabase/migrations/20260521000000_signal_durability.sql.
 *
 * Keep in sync with the migration when adding new values.
 * The Deno equivalents live in
 * supabase/functions/_shared/signal-status.ts.
 */

// ===========================================================
// Status
// ===========================================================

export const SIGNAL_STATUSES = [
  'active',
  'archived',
  'assigned',
  'won',
  'passed',
  'expired',
  'suppressed',
] as const;

export type SignalStatus = typeof SIGNAL_STATUSES[number];

export const SIGNAL_STATUS_LABELS: Record<SignalStatus, string> = {
  active: 'Active',
  archived: 'Archived',
  assigned: 'Assigned',
  won: 'Won',
  passed: 'Passed',
  expired: 'Expired',
  suppressed: 'Suppressed',
};

// Statuses that appear in the active feed.
export const FEED_VISIBLE_STATUSES: ReadonlySet<SignalStatus> = new Set([
  'active',
  'expired',
  'assigned',
]);

// Statuses that take a signal out of active circulation.
export const TERMINAL_STATUSES: ReadonlySet<SignalStatus> = new Set([
  'archived',
  'passed',
  'suppressed',
  'won',
]);

// ===========================================================
// Archive reasons
// ===========================================================

export const ARCHIVE_REASONS = [
  'outdated',
  'not_aligned',
  'pursuing_elsewhere',
  'client_passed',
  'not_relevant',
  'duplicate',
  'other',
] as const;

export type ArchiveReason = typeof ARCHIVE_REASONS[number];

export const ARCHIVE_REASON_LABELS: Record<ArchiveReason, string> = {
  outdated: 'Outdated',
  not_aligned: 'Not aligned with our focus',
  pursuing_elsewhere: 'Pursuing elsewhere',
  client_passed: 'Client passed',
  not_relevant: 'Not relevant',
  duplicate: 'Duplicate',
  other: 'Other (see notes)',
};

// Reasons surfaced in the archive dropdown, ordered for the UI.
export const ARCHIVE_REASON_ORDER: ReadonlyArray<ArchiveReason> = [
  'not_relevant',
  'not_aligned',
  'outdated',
  'pursuing_elsewhere',
  'client_passed',
  'duplicate',
  'other',
];

// ===========================================================
// Feedback action enum
// ===========================================================

export const SIGNAL_FEEDBACK_ACTIONS = [
  'archive',
  'restore',
  'assign',
  'unassign',
  'pass',
  'won',
  'duplicate_detected',
  'expired_at_ingest',
  'suppressed_at_ingest',
] as const;

export type SignalFeedbackAction = typeof SIGNAL_FEEDBACK_ACTIONS[number];

// ===========================================================
// Deadline source enum
// ===========================================================

export const DEADLINE_SOURCES = [
  'explicit',
  'source_metadata',
  'headline',
  'summary',
  'inferred',
  'none',
] as const;

export type DeadlineSource = typeof DEADLINE_SOURCES[number];

// ===========================================================
// Derived helpers
// ===========================================================

/**
 * Whether a signal with the given deadline should display the URGENT badge.
 * Mirrors the classifier logic in
 * supabase/functions/_shared/signal-classification.ts.
 */
export function isUrgent(deadline_at: string | Date | null | undefined, now: Date = new Date()): boolean {
  if (!deadline_at) return false;
  const d = deadline_at instanceof Date ? deadline_at : new Date(deadline_at);
  if (Number.isNaN(d.getTime())) return false;
  const deltaDays = (d.getTime() - now.getTime()) / 86400000;
  return deltaDays >= 0 && deltaDays <= 14;
}

/**
 * Whether a signal with the given status should be visible in the active feed.
 */
export function isFeedVisible(status: SignalStatus): boolean {
  return FEED_VISIBLE_STATUSES.has(status);
}

// ===========================================================
// Mutation payload shapes (for client-side calls to Edge Functions)
// ===========================================================

export interface ArchiveSignalRequest {
  signal_id: string;
  reason: ArchiveReason;
  notes?: string;
}

export interface RestoreSignalRequest {
  signal_id: string;
}
